import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { HouseLine, Clock, Warning, CheckCircle, Plus, Trash, CircleNotch, MapPin } from "@phosphor-icons/react";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

function CountdownCard({ label, days, deadline, threshold, testId }) {
  // color by urgency: past-due=rose, ≤14d=amber, ≤45d=emerald with amber-tint, further=neutral
  const past = days !== null && days < 0;
  const urgent = days !== null && days >= 0 && days <= threshold;
  const bar = past ? "bg-rose-500" : urgent ? "bg-amber-500" : "bg-emerald-500";
  const badge = past
    ? "bg-rose-50 text-rose-800 border-rose-200"
    : urgent
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-emerald-50 text-emerald-800 border-emerald-200";
  return (
    <div className={`rounded-md border p-3 ${past ? "border-rose-200 bg-rose-50/50" : urgent ? "border-amber-200 bg-amber-50/50" : "border-zinc-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Clock size={10} /> {label}</p>
        <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1.5 py-0.5 font-semibold ${badge}`}>{past ? `${Math.abs(days)}d overdue` : days === null ? "unset" : `${days}d left`}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <p className="font-heading text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Cardo', Georgia, serif" }}>{deadline || "—"}</p>
      </div>
      <div className="relative h-1 bg-zinc-100 rounded-full mt-2 overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${bar}`} style={{ width: `${Math.max(4, Math.min(100, 100 - (days || 0) * (100 / (threshold * 3))))}%` }} />
      </div>
    </div>
  );
}

export function Exchange1031Drawer({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: exchanges, isLoading } = useQuery({
    queryKey: ["exchanges-1031"],
    queryFn: () => api.get("/portal/exchanges").then((r) => r.data),
    enabled: open,
  });
  const [form, setForm] = useState({ relinquished_property: "", relinquished_close_date: "", sale_amount: "", identified_replacements: "" });

  const create = useMutation({
    mutationFn: () => api.post("/portal/exchanges", {
      relinquished_property: form.relinquished_property,
      relinquished_close_date: form.relinquished_close_date,
      sale_amount: parseFloat(form.sale_amount) || 0,
      identified_replacements: form.identified_replacements ? form.identified_replacements.split("\n").map((s) => s.trim()).filter(Boolean) : [],
    }),
    onSuccess: () => {
      toast.success("1031 exchange tracked — countdown started");
      setForm({ relinquished_property: "", relinquished_close_date: "", sale_amount: "", identified_replacements: "" });
      qc.invalidateQueries({ queryKey: ["exchanges-1031"] });
    },
    onError: (e) => toast.error(errText(e)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/portal/exchanges/${id}`),
    onSuccess: () => { toast.success("Exchange removed"); qc.invalidateQueries({ queryKey: ["exchanges-1031"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="exchange-drawer">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-indigo-100 flex items-center justify-center">
              <HouseLine size={16} weight="fill" className="text-indigo-700" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-indigo-700">Real estate</p>
              <SheetTitle className="font-heading text-lg leading-tight">1031 Exchange tracker</SheetTitle>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-1">45-day identification &amp; 180-day closing deadlines for every like-kind exchange.</p>
        </SheetHeader>

        {isLoading ? (
          <p className="text-xs text-zinc-400 mt-6 flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin" /> Loading exchanges…</p>
        ) : (
          <div className="mt-5 space-y-5">
            {(exchanges || []).length === 0 && (
              <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/60 px-3 py-6 text-center">
                <HouseLine size={24} className="text-zinc-400 mx-auto mb-1" />
                <p className="text-sm text-slate-800 font-medium">No active exchanges</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">Add your first 1031 below — we'll start the 45/180-day countdown automatically.</p>
              </div>
            )}

            {(exchanges || []).map((x) => (
              <div key={x.id} data-testid={`exchange-${x.id}`} className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><MapPin size={13} weight="fill" className="text-indigo-500" /> {x.relinquished_property}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">Closed {x.relinquished_close_date} · Sale <span className="font-mono">{money(x.sale_amount)}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1.5 py-0.5 ${x.status === "completed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : x.status === "failed" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{x.status}</span>
                    <button data-testid={`exchange-delete-${x.id}`} onClick={() => del.mutate(x.id)} className="text-zinc-400 hover:text-rose-600 transition-colors duration-150"><Trash size={13} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <CountdownCard label="Identification deadline" days={x.days_to_id} deadline={x.id_deadline} threshold={14} testId={`countdown-id-${x.id}`} />
                  <CountdownCard label="Closing deadline" days={x.days_to_close} deadline={x.close_deadline} threshold={45} testId={`countdown-close-${x.id}`} />
                </div>
                {(x.identified_replacements || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Identified replacements ({x.identified_replacements.length})</p>
                    <ul className="text-xs text-slate-700 space-y-0.5">
                      {x.identified_replacements.map((rep, i) => (
                        <li key={i} className="flex items-center gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-500" /> {rep}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {x.days_to_id !== null && x.days_to_id >= 0 && x.days_to_id <= 14 && (x.identified_replacements || []).length === 0 && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5">
                    <Warning size={11} weight="fill" className="text-amber-600 mt-0.5" />
                    <p className="text-[10px] text-amber-800">Only {x.days_to_id} days left to identify replacement property. Notify your qualified intermediary now.</p>
                  </div>
                )}
              </div>
            ))}

            <section className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-4" data-testid="exchange-form">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Start a new 1031 exchange</p>
              <div className="space-y-2">
                <Input data-testid="ex-relinquished" placeholder="Relinquished property (address)" value={form.relinquished_property} onChange={(e) => setForm({ ...form, relinquished_property: e.target.value })} className="h-8 text-xs" />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="ex-close-date" type="date" value={form.relinquished_close_date} onChange={(e) => setForm({ ...form, relinquished_close_date: e.target.value })} className="h-8 text-xs" />
                  <Input data-testid="ex-sale-amount" type="number" placeholder="Sale amount USD" value={form.sale_amount} onChange={(e) => setForm({ ...form, sale_amount: e.target.value })} className="h-8 text-xs" />
                </div>
                <textarea data-testid="ex-replacements" placeholder="Identified replacement addresses, one per line (optional)" value={form.identified_replacements} onChange={(e) => setForm({ ...form, identified_replacements: e.target.value })} className="w-full text-xs border border-zinc-200 rounded-md p-2 min-h-[54px] bg-white" />
                <Button data-testid="ex-add-btn" size="sm" className="w-full h-8" disabled={!form.relinquished_property || !form.relinquished_close_date || create.isPending} onClick={() => create.mutate()}>
                  {create.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Plus size={13} className="mr-1" />} Start tracking (auto 45/180-day)
                </Button>
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
